/**
 * src/sync/flags.js — Firestore-backed feature flags for shadow-write rollout (B5).
 *
 * Contract
 * --------
 * - Flag docs live at `feature_flags/{flag_id}`.
 *   - `flag_id` for per-collection shadow-write is `shadow_write_{collection}`,
 *     e.g. `shadow_write_teams`.
 *   - Doc shape: `{ enabled: boolean, updatedAt: serverTimestamp }`.
 *   - Missing doc, empty doc, or `enabled !== true` → flag is OFF.
 * - The frontend NEVER creates these docs. They are managed by an admin script
 *   or admin UI (Etap 3+). Until then `isShadowEnabled()` returns false for
 *   every collection — guaranteeing zero behavior change at runtime.
 * - We attach ONE collection-level `onSnapshot` listener to `feature_flags`
 *   the first time `isShadowEnabled()` is called. Propagation latency to all
 *   tabs is < 5s (Firestore typical), satisfying the B5 < 5s revert SLA.
 *
 * Reactivity
 * ----------
 * - The shared cache is a Vue `reactive()` proxy so that **Vue `computed()`
 *   and template `v-if`** consumers (e.g. `showBackendButtons` in
 *   CreatePdf.vue) re-evaluate when the `onSnapshot` listener mutates it.
 *   Without this, the listener updates the JS object but Vue never re-renders.
 * - Mutations are in-place (`delete` + assignment) — re-assigning the cache
 *   variable would replace the reactive proxy reference and Vue would lose
 *   tracking on the old proxy.
 * - Imperative consumers (e.g. `shadowWriter.js` calling `isShadowEnabled()`
 *   synchronously per write) are unaffected — they read the current value at
 *   call time, which is exactly what a reactive read returns.
 *
 * Safety
 * ------
 * - All accessors are synchronous and never throw. If Firestore is unreachable
 *   or the listener errors out, every collection is reported OFF (fail-closed).
 * - Listener is lazy — importing this module does not subscribe to anything.
 *   Tests can fully control the cache via `_setFlagForTest()` / `_resetForTest()`.
 */

import { reactive } from 'vue';
import { db } from '@/firebase/config';
import { collection, onSnapshot } from 'firebase/firestore';

const COLLECTION_PATH = 'feature_flags';
const SHADOW_PREFIX = 'shadow_write_';

// Upper bound on how long a write path will block waiting for the first flag
// snapshot before proceeding fail-closed. Chosen > typical Firestore first-load
// latency (< 5s, the B5 SLA) with headroom, but short enough that a permanently
// stuck listener (offline/blocked) can't wedge the write path. On timeout we
// mark hydrated so subsequent calls short-circuit instead of re-racing.
const FLAGS_READY_TIMEOUT_MS = 8000;

// Reactive cache: { [flag_id]: { enabled: boolean } }. MUST be mutated in-place
// — never reassign this `const` reference, or Vue computeds lose track of it.
const _cache = reactive(Object.create(null));
let _unsubscribe = null;
let _listenerStarted = false;
let _listenerError = null;

// Hydration tracking (B5 race fix). The onSnapshot listener fills `_cache`
// ASYNCHRONOUSLY; a synchronous `isShadowEnabled()` call before the first
// snapshot arrives reads an empty cache and (correctly, fail-closed) reports
// OFF. That window silently dropped shadow-writes for the FIRST write after a
// fresh page load — see `whenFlagsReady()` / shadowWriter.dualWrite. We expose
// a promise that resolves once the first snapshot (or listener error, or a
// test override) has settled, so write paths can await a determinate flag.
let _hydrated = false;
let _readyResolve = null;
let _readyPromise = null;

function _ensureReadyPromise() {
  if (!_readyPromise) {
    _readyPromise = new Promise((resolve) => { _readyResolve = resolve; });
  }
  return _readyPromise;
}

// Mark the cache as having received its first authoritative state (snapshot,
// error, or test override) and release anyone awaiting whenFlagsReady().
function _markHydrated() {
  _hydrated = true;
  if (_readyResolve) {
    const r = _readyResolve;
    _readyResolve = null;
    r();
  }
}

function flagIdFor(collectionName) {
  return `${SHADOW_PREFIX}${collectionName}`;
}

// In-place "replace whole cache" helper — needed by listener + tests because
// the cache is reactive and we cannot drop the proxy reference.
function _replaceCache(nextEntries) {
  for (const k of Object.keys(_cache)) delete _cache[k];
  for (const k of Object.keys(nextEntries)) _cache[k] = nextEntries[k];
}

/**
 * Lazily start the Firestore listener. Safe to call repeatedly — only the
 * first call actually subscribes.
 *
 * If Firestore throws synchronously (mis-init), we record the error and
 * report fail-closed; we do NOT retry — the next page load gets a clean slate.
 */
function ensureListenerStarted() {
  if (_listenerStarted) return;
  _listenerStarted = true;
  // Create the readiness promise BEFORE subscribing so the callbacks below can
  // resolve it on the first snapshot/error.
  _ensureReadyPromise();
  try {
    const colRef = collection(db, COLLECTION_PATH);
    _unsubscribe = onSnapshot(
      colRef,
      (snap) => {
        // Rebuild cache from snapshot — handles add/update/remove uniformly.
        const next = Object.create(null);
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          next[docSnap.id] = { enabled: data.enabled === true };
        });
        _replaceCache(next);
        _markHydrated();
      },
      (err) => {
        // Listener failed (rules denial / network) → drop to fail-closed.
        // Surface for debugging but do not console.error in tests. Still mark
        // hydrated: the state is now determinate (everything OFF), so awaiters
        // proceed instead of blocking until the timeout.
        _listenerError = err;
        _replaceCache(Object.create(null));
        _markHydrated();
      }
    );
  } catch (err) {
    _listenerError = err;
    // Synchronous mis-init is also a determinate (fail-closed) outcome.
    _markHydrated();
  }
}

/**
 * Returns true iff Firestore flag `shadow_write_{collection}` is set to
 * `{ enabled: true }`. Default OFF for missing/unknown/error cases.
 */
export function isShadowEnabled(collectionName) {
  if (!collectionName) return false;
  ensureListenerStarted();
  const entry = _cache[flagIdFor(collectionName)];
  return !!(entry && entry.enabled === true);
}

/**
 * Generic flag accessor by FULL flag id (no prefix applied). Use this for flags
 * outside the `shadow_write_` convention — e.g. `use_backend_pdf_generation`.
 * Default OFF for missing/unknown/error cases. Reactive: the shared `_cache` is
 * kept up to date by the same `onSnapshot` listener as `isShadowEnabled()`.
 */
export function isFlagEnabled(flagId) {
  if (!flagId) return false;
  ensureListenerStarted();
  const entry = _cache[flagId];
  return !!(entry && entry.enabled === true);
}

/**
 * Resolve once the feature-flag cache has received its first authoritative
 * state from Firestore (or the listener has errored / mis-initialized, both of
 * which are determinate fail-closed outcomes). Starts the listener if needed.
 *
 * Write paths (shadowWriter.dualWrite) MUST await this before reading a flag,
 * otherwise a write fired in the sub-second window between page load and the
 * first snapshot reads an empty cache, sees the flag as OFF, and silently drops
 * the shadow-write (it never even reaches the durable outbox). See the B5 race
 * note at the top of this module.
 *
 * @param {number} [timeoutMs=FLAGS_READY_TIMEOUT_MS] Upper bound so a
 *   permanently-stuck listener (offline, blocked) can't wedge the write path
 *   forever. On timeout we mark the cache hydrated (determinate fail-closed) so
 *   this and every subsequent call short-circuit instead of re-racing a fresh
 *   timer; a late snapshot still corrects the cache afterwards. Pass 0/null to
 *   wait indefinitely (tests).
 * @returns {Promise<void>}
 */
export function whenFlagsReady(timeoutMs = FLAGS_READY_TIMEOUT_MS) {
  ensureListenerStarted();
  if (_hydrated) return Promise.resolve();
  const ready = _ensureReadyPromise();
  if (!timeoutMs || timeoutMs <= 0) return ready;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      // Timed out waiting for the first snapshot. Flip to hydrated so we don't
      // leave _readyPromise pending and re-race a new timer on the next call.
      _markHydrated();
      resolve();
    }, timeoutMs);
    // Snapshot/error arrived first → cancel the timer and resolve.
    ready.then(() => {
      clearTimeout(timer);
      resolve();
    });
  });
}

/**
 * Snapshot of currently-enabled shadow-write collections. Mostly useful for
 * diagnostics (e.g. a hidden /diag panel in Etap 3).
 */
export function listEnabledShadowCollections() {
  ensureListenerStarted();
  const out = [];
  for (const key of Object.keys(_cache)) {
    if (key.startsWith(SHADOW_PREFIX) && _cache[key].enabled === true) {
      out.push(key.slice(SHADOW_PREFIX.length));
    }
  }
  return out;
}

/**
 * Last error observed by the snapshot listener, or null. Helps surface
 * Firestore rules denials during dev.
 */
export function getFlagsListenerError() {
  return _listenerError;
}

/**
 * Stop the listener — Vue app rarely calls this (singleton lifetime), but
 * tests and HMR cleanup do.
 */
export function stopFlagsListener() {
  if (_unsubscribe) {
    try { _unsubscribe(); } catch (_) { /* ignore */ }
  }
  _unsubscribe = null;
  _listenerStarted = false;
}

// ─── Test-only helpers ─────────────────────────────────────────────────────
// Exposed for Vitest; not intended for application code. Underscore prefix
// signals "internal".

export function _setFlagForTest(collectionName, enabled) {
  _cache[flagIdFor(collectionName)] = { enabled: enabled === true };
  // Also short-circuit listener startup so tests don't need a Firestore mock.
  _listenerStarted = true;
  // A test-set flag is a determinate state — whenFlagsReady() resolves at once.
  _markHydrated();
}

// Set a flag by its FULL id (no prefix) — for testing isFlagEnabled() with
// non-shadow flags like `use_backend_pdf_generation`.
export function _setRawFlagForTest(flagId, enabled) {
  _cache[flagId] = { enabled: enabled === true };
  _listenerStarted = true;
  _markHydrated();
}

export function _resetForTest() {
  if (_unsubscribe) {
    try { _unsubscribe(); } catch (_) { /* ignore */ }
  }
  _unsubscribe = null;
  _listenerStarted = false;
  _listenerError = null;
  _hydrated = false;
  _readyPromise = null;
  _readyResolve = null;
  _replaceCache(Object.create(null));
}

// Re-export the snapshot callback shape for tests that want to simulate
// Firestore propagation without mocking the SDK end-to-end.
export const __internals = {
  flagIdFor,
  // Exposed so tests can simulate an onSnapshot callback manually.
  _applySnapshot(docs) {
    const next = Object.create(null);
    for (const d of docs) {
      next[d.id] = { enabled: d.data && d.data.enabled === true };
    }
    _replaceCache(next);
    _listenerStarted = true;
    _markHydrated();
  },
};
